import { ref, computed, watch } from '@vue/composition-api'
import analytics, {
  getSelectionOrWishlistMixpanelObj,
} from '@/@core/utils/analytics'
import {
  apiToastError,
  apiToastSuccess,
} from '@/@core/utils/toast'
import {
  formatObject,
  getWishlistTitle,
  parseErrorObject,
  searchDirectRetailer,
} from '@/@core/utils/utils'
import { kpEndpoint, kpParams, kpRequest } from '@/axios'
import constants, { DOWNLOAD_TYPES, FILE_FORMATS } from '@/constants'
import store from '@/store'
import { DELETE_WISHLIST, DOWNLOAD_WISHLIST } from '@/store/modules/shop.module'
import { constants as c } from '@kingpin-global/kingpin-utils-frontend'
import { SHOW_SALES_ANALYTICS_NAV_INDICATOR } from '@/store/modules/notification.module'
import { MULTI_SELECT_MODAL_ID, RESET_SELECTED_SELECTIONS, SET_SELECTED_RETAILER } from '@/store/modules/multi-select.module'

const { DOWNLOAD_OPTIONS } = constants

const { ROLES } = c
const CART_WITH_MULTIPLE_STORES = constants.TRACKS.ACTIONS.CART_WITH_MULTIPLE_STORES
const { SHARE_MULTIPLE_SELECTIONS } = constants.TRACKS.ACTIONS

export function useWishlistAction(wishlistRef, isMultipleSelectionsRef) {
  // Reactive state
  const wishlistNewName = ref(wishlistRef.value?.name || '')
  const isDownloading = ref(false)
  const selectedRetailer = ref(null)
  const email = ref('')
  const isSharing = ref(false)
  const allDirectRetailers = ref([])
  const searchText = ref('')
  const retailerId = ref(null)

  // Computed
  const isBrand = computed(() => store.getters.isBrand)
  const isRetailer = computed(() => store.getters.isRetailer)
  const directRetailers = computed(() => store.state.connections.directRetailers || [])
  const profileData = computed(() => store.state.auth.profileData)
  const wishlists = computed(() => store.state.shop.wishlists)
  const selectedSelections = computed(() => store.getters?.getSelectedSelections || [])

  const getShareWislistModalId = computed(() => (isMultipleSelectionsRef.value
    ? MULTI_SELECT_MODAL_ID
    : `modal-share-wishlist-${wishlistRef.value?._id}`))

  const storesList = computed(() => profileData.value?.userAssociations
    ?.filter(userAsso => userAsso.entityType === ROLES.RETAILER)
    .map(userAsso => ({
      storeId: userAsso.entityId,
      storeName: userAsso?.entity?.entityName,
    })))

  const shareSelectionSuccessMessage = computed(
    () => `${getWishlistTitle(isMultipleSelectionsRef.value)} has been shared successfully`,
  )

  // Watchers
  watch(directRetailers, () => {
    allDirectRetailers.value = searchDirectRetailer(directRetailers.value)
  }, { deep: true })

  watch(searchText, () => {
    searchRetailer()
  })

  // Init
  function initWishlistAction() {
    allDirectRetailers.value = searchDirectRetailer(directRetailers.value)
  }

  // Methods
  function resetInputs() {
    email.value = ''
    selectedRetailer.value = null
    retailerId.value = null
  }

  function isAlreadySelected(retailer) {
    return selectedRetailer.value?.email === retailer.email
  }

  function toggleRetailerSelection(retailer) {
    if (isAlreadySelected(retailer)) {
      selectedRetailer.value = null
      retailerId.value = null
      email.value = ''
    } else {
      retailerId.value = retailer.retailerId
      selectedRetailer.value = retailer
      email.value = retailer.email
    }
  }

  function buildRetailerEventPayload(retailer) {
    const eventPayload = { retailer }
    if (isMultipleSelectionsRef.value) {
      eventPayload.selections = getSelectedSelections()
    } else {
      eventPayload.selection = wishlistRef.value
    }
    return eventPayload
  }

  function onSelectRetailer(retailer) {
    toggleRetailerSelection(retailer)
    store.commit(SET_SELECTED_RETAILER, retailer)
    searchText.value = ''
    analytics.track(
      constants.TRACKS.ACTIONS.BRAND_SELECT_EMAIL_TO_SHARE_SELECTION,
      buildRetailerEventPayload(retailer),
    )
  }

  function searchRetailer() {
    allDirectRetailers.value = searchDirectRetailer(
      directRetailers.value,
      searchText.value,
    )
  }

  function onCloseShareWishlist() {
    resetInputs()
    analytics.track(SHARE_MULTIPLE_SELECTIONS.BRAND_CLICKS_CANCEL_BTN_ON_SHARE_POPUP)
  }

  function renameWishlist() {
    return kpRequest({
      ...kpParams(kpEndpoint.wishlist.rename, wishlistRef.value._id),
      payload: {
        name: wishlistNewName.value,
      },
    })
      .then(() => {
        wishlistRef.value.name = wishlistNewName.value
        apiToastSuccess(`${getWishlistTitle()} renamed successfully`)
        return { success: true }
      })
      .catch(err => {
        apiToastError(err)
        return { success: false }
      })
  }

  function buildPayload() {
    const payload = {
      email: email.value,
      selectionIds: selectedSelections.value,
    }
    if (retailerId.value) {
      payload.retailerId = retailerId.value
    }
    return payload
  }

  function getSelectedSelections() {
    return selectedSelections.value.map(
      selectionId => wishlists.value.find(w => w._id === selectionId),
    )
  }

  function handleSuccessMultipleShare(emitFetchWishlists, emitOnOpenErrorsPopup, hideModalFn) {
    isSharing.value = false
    store.commit(SHOW_SALES_ANALYTICS_NAV_INDICATOR)
    handlePartialSuccessMultipleShare(null, emitFetchWishlists, emitOnOpenErrorsPopup)
    emitFetchWishlists()
    hideModalFn()
    resetInputs()
    store.commit(RESET_SELECTED_SELECTIONS)
  }

  function handleErrorMultipleShare(err, emitFetchWishlists, emitOnOpenErrorsPopup, hideModalFn) {
    isSharing.value = false
    if (isBadRequestWithErrors(err)) {
      handlePartialSuccessMultipleShare(err, emitFetchWishlists, emitOnOpenErrorsPopup)
    } else {
      apiToastError(err)
      analytics.track(SHARE_MULTIPLE_SELECTIONS.BRAND_SHARED_SELECTION_FAILED, { error: parseErrorObject(err) })
    }
    hideModalFn()
    resetInputs()
    store.commit(RESET_SELECTED_SELECTIONS)
  }

  function isBadRequestWithErrors(err) {
    return err?.response?.status === 400 && err?.response?.data?.data?.length
  }

  function handlePartialSuccessMultipleShare(err, emitFetchWishlists, emitOnOpenErrorsPopup) {
    const selectedWishlists = getSelectedSelections()
    if (!err) {
      emitOnOpenErrorsPopup(selectedWishlists)
      return
    }
    const errors = err?.response?.data?.data
    const errorMessages = selectedWishlists.map(w => {
      const error = errors.find(e => e.selectionId === w._id)
      return {
        _id: w._id,
        isError: !!error,
        name: w?.name,
      }
    })
    emitFetchWishlists()
    emitOnOpenErrorsPopup(errorMessages)
    analytics.track(SHARE_MULTIPLE_SELECTIONS.BRAND_SHARED_SELECTION_FAILED, { errorMessages })
  }

  function shareMultipleSelections(emitFetchWishlists, emitOnOpenErrorsPopup, hideModalFn) {
    isSharing.value = true
    const payload = buildPayload()
    analytics.track(
      SHARE_MULTIPLE_SELECTIONS.BRAND_CLICKS_SHARE_SELECTION_BTN_ON_SHARE_POPUP, { payload },
    )
    kpRequest({
      ...kpEndpoint.wishlist.shareMultiple,
      payload,
    })
      .then(() => {
        handleSuccessMultipleShare(emitFetchWishlists, emitOnOpenErrorsPopup, hideModalFn)
        analytics.track(SHARE_MULTIPLE_SELECTIONS.BRAND_SHARED_SELECTION_SUCCESSFULLY, payload)
      })
      .catch(err => {
        handleErrorMultipleShare(err, emitFetchWishlists, emitOnOpenErrorsPopup, hideModalFn)
      })
  }

  function handleShareSuccess(payload, emitFetchWishlists, hideModalFn) {
    isSharing.value = false
    apiToastSuccess(shareSelectionSuccessMessage.value)
    store.commit(SHOW_SALES_ANALYTICS_NAV_INDICATOR)
    emitFetchWishlists()
    hideModalFn()
    resetInputs()
    analytics.track(constants.TRACKS.ACTIONS.SELCTION_SHARED_SUCCESSFULLY, payload)
  }

  function handleShareError(err, emitFetchWishlists, emitOpenReferralModel, hideModalFn) {
    isSharing.value = false
    const isReferralRequired = err.response?.status === 441
    if (!isReferralRequired) {
      apiToastError(err)
      return
    }
    emitOpenReferralModel(email.value, wishlistRef.value)
    resetInputs()
    emitFetchWishlists()
    hideModalFn()
  }

  function buildSharePayload() {
    const payload = { email: email.value }
    if (retailerId.value) {
      payload.retailerId = retailerId.value
    }
    return payload
  }

  function shareWishlist(emitFetchWishlists, emitOpenReferralModel, hideModalFn) {
    analytics.track(
      constants.TRACKS.ACTIONS.BRAND_CLICKS_SHARE_SELECTION_BTN, { selection: wishlistRef.value },
    )
    isSharing.value = true
    const payload = buildSharePayload()
    kpRequest({
      ...kpParams(kpEndpoint.wishlist.share, wishlistRef.value._id),
      payload,
    })
      .then(() => handleShareSuccess(payload, emitFetchWishlists, hideModalFn))
      .catch(err => handleShareError(err, emitFetchWishlists, emitOpenReferralModel, hideModalFn))
  }

  function onClickShareWishlist(emitFetchWishlists, emitOpenReferralModel, emitOnOpenErrorsPopup, hideModalFn) {
    if (isMultipleSelectionsRef.value) {
      shareMultipleSelections(emitFetchWishlists, emitOnOpenErrorsPopup, hideModalFn)
    } else {
      shareWishlist(emitFetchWishlists, emitOpenReferralModel, hideModalFn)
    }
  }

  function isViewingDeletedWishlist(routerInstance, deletedWishlistId) {
    if (!routerInstance) {
      return false
    }
    const currentWishlist = store.getCurrentCheckoutWishlist
    const isCurrentWishlist = currentWishlist?._id === deletedWishlistId
    const productRoutes = ['wishlist-products', 'selection-products']
    const isOnProductRoute = productRoutes.includes(routerInstance.currentRoute.name)
    return isCurrentWishlist && isOnProductRoute
  }

  function redirectAfterDelete(routerInstance) {
    const routeName = isRetailer.value ? 'wishlist' : 'selection'
    routerInstance.push({ name: routeName })
  }

  function trackWishlistDeletion() {
    const trackAction = isBrand.value
      ? constants.TRACKS.ACTIONS.BRAND_DELETES_SELECTION
      : constants.TRACKS.ACTIONS.RETAILER_DELETES_WISHLIST
    analytics.track(
      trackAction,
      formatObject(
        getSelectionOrWishlistMixpanelObj(wishlistRef.value, isBrand.value),
      ),
    )
  }

  function deleteWishlist(routerInstance) {
    return store
      .dispatch(DELETE_WISHLIST, wishlistRef.value._id)
      .then(() => {
        if (isViewingDeletedWishlist(routerInstance, wishlistRef.value._id)) {
          redirectAfterDelete(routerInstance)
        }
        apiToastSuccess(`${getWishlistTitle()} deleted successfully`)
        trackWishlistDeletion()
        return { success: true }
      })
      .catch(() => ({ success: false }))
  }

  function buildOrderFormPayload(selectedStores) {
    return {
      downloadType: DOWNLOAD_TYPES.WISHLIST_EXPORT.VALUE,
      respectiveId: wishlistRef.value._id,
      fileType: FILE_FORMATS.XLSX,
      entityIds: selectedStores.map(s => s.storeId),
    }
  }

  function handleOrderFormSuccess(res, {
 payload, params, notifyDownloadsNavMenu, hideModalFn,
}) {
    isDownloading.value = false
    hideModalFn()
    apiToastSuccess(res.data.message)
    notifyDownloadsNavMenu()
    analytics.track(CART_WITH_MULTIPLE_STORES.RETAILER_EXPORT_WISHLIST_AS_ORDER_FORM, { payload, params })
  }

  function handleOrderFormError(err, selectedStores, notifyDownloadsNavMenu, hideModalFn) {
    const isMultiStoreBadRequest = err.response?.status === 400 && selectedStores.length > 1
    if (isMultiStoreBadRequest) {
      notifyDownloadsNavMenu()
    }
    isDownloading.value = false
    hideModalFn()
  }

  function downloadOrderForm(selectedStores, notifyDownloadsNavMenu, hideModalFn) {
    isDownloading.value = true
    const payload = buildOrderFormPayload(selectedStores)
    const params = { type: DOWNLOAD_OPTIONS.WISHLIST[2].value }

    store
      .dispatch(DOWNLOAD_WISHLIST, { payload, params })
      .then(res => handleOrderFormSuccess(res, {
 payload, params, notifyDownloadsNavMenu, hideModalFn,
}))
      .catch(err => handleOrderFormError(err, selectedStores, notifyDownloadsNavMenu, hideModalFn))
  }

  function isOrderFormFormat(selectedFileFormat) {
    return selectedFileFormat.value === DOWNLOAD_OPTIONS.WISHLIST[2].value
  }

  function buildDownloadPayload(selectedFileFormat) {
    const downloadType = DOWNLOAD_TYPES.WISHLIST_EXPORT.VALUE
    const payload = {
      downloadType,
      respectiveId: wishlistRef.value._id,
      fileType: selectedFileFormat.fileType,
    }
    const params = selectedFileFormat.params || {}
    const isBrandOrderForm = isBrand.value && isOrderFormFormat(selectedFileFormat)
    if (isBrandOrderForm) {
      params.isOrderForm = true
    }
    return { payload, params }
  }

  function trackRetailerDownload(selectedFileFormat) {
    if (!isRetailer.value) {
      return
    }
    analytics.track(
      constants.TRACKS.ACTIONS.RETAILER_DOWNLOADS_WISHLIST,
      formatObject(
        getSelectionOrWishlistMixpanelObj(wishlistRef.value, false, {
          downloadFileOption: selectedFileFormat,
        }),
      ),
    )
  }

  function handleDownloadSuccess(res, notifyDownloadsNavMenu, hideDownloadModalFn) {
    isDownloading.value = false
    hideDownloadModalFn()
    apiToastSuccess(res.data.message)
    notifyDownloadsNavMenu()
  }

  function handleDownloadError(hideDownloadModalFn) {
    isDownloading.value = false
    hideDownloadModalFn()
  }

  function downloadWishlist(selectedFileFormat, notifyDownloadsNavMenu, hideDownloadModalFn, showSelectStoresModalFn) {
    const isRetailerOrderForm = isRetailer.value && isOrderFormFormat(selectedFileFormat)
    if (isRetailerOrderForm) {
      hideDownloadModalFn()
      showSelectStoresModalFn()
      return
    }
    isDownloading.value = true
    const { payload, params } = buildDownloadPayload(selectedFileFormat)
    store
      .dispatch(DOWNLOAD_WISHLIST, { payload, params })
      .then(res => handleDownloadSuccess(res, notifyDownloadsNavMenu, hideDownloadModalFn))
      .catch(() => handleDownloadError(hideDownloadModalFn))
    trackRetailerDownload(selectedFileFormat)
  }

  return {
    // State
    wishlistNewName,
    isDownloading,
    selectedRetailer,
    email,
    isSharing,
    allDirectRetailers,
    searchText,
    retailerId,

    // Computed
    directRetailers,
    profileData,
    wishlists,
    selectedSelections,
    getShareWislistModalId,
    storesList,
    shareSelectionSuccessMessage,

    // Methods
    initWishlistAction,
    resetInputs,
    onSelectRetailer,
    searchRetailer,
    onCloseShareWishlist,
    renameWishlist,
    shareWishlist,
    shareMultipleSelections,
    onClickShareWishlist,
    deleteWishlist,
    downloadOrderForm,
    downloadWishlist,

    // Constants
    getWishlistTitle,
    DOWNLOAD_OPTIONS,
  }
}
